import { create } from "zustand";
import {
  readThemePreference,
  resolveTheme,
  systemPrefersLight,
  type ResolvedTheme,
  type ThemePreference,
  writeThemePreference
} from "./theme.js";

export type LiveState = "connecting" | "live" | "reconnecting" | "offline";
export type { ThemePreference } from "./theme.js";
export type ExperimentTelemetryMode = "single" | "compare";
export type CockpitView = "requirement" | "evolution" | "runs" | "reports" | "paper" | "resources";

type StoredExperimentComparison = {
  version: 1;
  mode: ExperimentTelemetryMode;
  metric: string;
  runIds: string[];
};

const experimentComparisonStorageKey = "roboscientist.experiments.comparison";
const emptyExperimentComparison: StoredExperimentComparison = { version: 1, mode: "single", metric: "", runIds: [] };

const readExperimentComparison = (): StoredExperimentComparison => {
  try {
    const parsed = JSON.parse(localStorage.getItem(experimentComparisonStorageKey) ?? "null") as Partial<StoredExperimentComparison> | null;
    const metric = typeof parsed?.metric === "string" ? parsed.metric : "";
    const runIds = metric && Array.isArray(parsed?.runIds)
      ? [...new Set(parsed.runIds.filter((runId): runId is string => typeof runId === "string" && Boolean(runId)))].slice(0, 6)
      : [];
    return {
      version: 1,
      mode: parsed?.mode === "compare" ? "compare" : "single",
      metric,
      runIds
    };
  } catch {
    return emptyExperimentComparison;
  }
};

const persistExperimentComparison = (comparison: StoredExperimentComparison) => {
  try {
    localStorage.setItem(experimentComparisonStorageKey, JSON.stringify(comparison));
  } catch {
    // Keep the in-memory comparison usable when storage is unavailable.
  }
};

const browserStorage = (() => {
  try {
    return typeof window === "undefined" ? undefined : window.sessionStorage;
  } catch {
    return undefined;
  }
})();
const initialTheme = readThemePreference(browserStorage);
const initialResolvedTheme = resolveTheme(initialTheme, systemPrefersLight());
const initialExperimentComparison = readExperimentComparison();

type CockpitStore = {
  selectedTopicId: string | null;
  selectedThreadId: string | null;
  selectedRunId: string | null;
  cockpitView: CockpitView;
  eventCursor: number;
  liveState: LiveState;
  theme: ThemePreference;
  resolvedTheme: ResolvedTheme;
  experimentTelemetryMode: ExperimentTelemetryMode;
  experimentComparisonMetric: string;
  experimentComparisonRunIds: string[];
  selectTopic: (id: string | null) => void;
  selectThread: (id: string | null) => void;
  selectRun: (id: string | null) => void;
  setCockpitView: (view: CockpitView) => void;
  setEventCursor: (cursor: number) => void;
  setLiveState: (state: LiveState) => void;
  setTheme: (theme: ThemePreference) => void;
  setResolvedTheme: (theme: ResolvedTheme) => void;
  setExperimentTelemetryMode: (mode: ExperimentTelemetryMode) => void;
  setExperimentComparison: (metric: string, runIds: string[]) => void;
};

export const useCockpitStore = create<CockpitStore>((set) => ({
  selectedTopicId: null,
  selectedThreadId: null,
  selectedRunId: null,
  cockpitView: "evolution",
  eventCursor: Number(localStorage.getItem("roboscientist.eventCursor") ?? 0) || 0,
  liveState: "connecting",
  theme: initialTheme,
  resolvedTheme: initialResolvedTheme,
  experimentTelemetryMode: initialExperimentComparison.mode,
  experimentComparisonMetric: initialExperimentComparison.metric,
  experimentComparisonRunIds: initialExperimentComparison.runIds,
  selectTopic: (selectedTopicId) => set({ selectedTopicId }),
  selectThread: (selectedThreadId) => set({ selectedThreadId }),
  selectRun: (selectedRunId) => set({ selectedRunId }),
  setCockpitView: (cockpitView) => set({ cockpitView }),
  setEventCursor: (eventCursor) => {
    localStorage.setItem("roboscientist.eventCursor", String(eventCursor));
    set({ eventCursor });
  },
  setLiveState: (liveState) => set({ liveState }),
  setTheme: (theme) => {
    writeThemePreference(theme, browserStorage);
    set({ theme });
  },
  setResolvedTheme: (resolvedTheme) => set({ resolvedTheme }),
  setExperimentTelemetryMode: (experimentTelemetryMode) => set((state) => {
    persistExperimentComparison({
      version: 1,
      mode: experimentTelemetryMode,
      metric: state.experimentComparisonMetric,
      runIds: state.experimentComparisonRunIds
    });
    return { experimentTelemetryMode };
  }),
  setExperimentComparison: (experimentComparisonMetric, experimentComparisonRunIds) => set((state) => {
    const uniqueRunIds = experimentComparisonMetric ? [...new Set(experimentComparisonRunIds)].slice(0, 6) : [];
    persistExperimentComparison({
      version: 1,
      mode: state.experimentTelemetryMode,
      metric: experimentComparisonMetric,
      runIds: uniqueRunIds
    });
    return { experimentComparisonMetric, experimentComparisonRunIds: uniqueRunIds };
  })
}));
